import type { Circuit, PlayerTrackPosition } from '@multirx/shared/types/track.js';
import type { RaceHistoryPlayerSnapshot } from '@multirx/shared/types/raceHistory.js';
import {
  buildTrackWaypointsToTarget,
  densifyTrackWaypoints,
  trackPositionsEqual,
} from '@multirx/shared/game/trackMovement.js';

export function snapshotToTrackPosition(snap: RaceHistoryPlayerSnapshot): PlayerTrackPosition {
  return {
    mainPathIndex: snap.pathIndex,
    jokerBranchIndex: snap.jokerBranchIndex ?? null,
    startLaneIndex: snap.startLaneIndex ?? null,
  };
}

export type ArchiveWaypointOptions = {
  fromJokerLapUsed?: boolean;
  toJokerLapUsed?: boolean;
  /** Cases réellement parcourues ce tour (heuristique archives sans jokerLapUsed). */
  effectiveMove?: number | null;
};

function waypointBudget(circuit: Circuit): number {
  return (
    circuit.path.length +
    (circuit.jokerLap?.cells.length ?? 0) +
    (circuit.startLane?.cells.length ?? 0) +
    4
  );
}

/**
 * Détermine si le trajet from→to doit emprunter le joker.
 * Aligné sur GameScene : `targetJokerLapUsed` quand le flag passe false→true,
 * ou si une extrémité est déjà sur la branche.
 */
export function resolveArchiveTargetJokerLapUsed(
  circuit: Circuit,
  from: PlayerTrackPosition,
  to: PlayerTrackPosition,
  options: ArchiveWaypointOptions = {}
): boolean {
  if (to.jokerBranchIndex != null || from.jokerBranchIndex != null) return true;

  const fromUsed = options.fromJokerLapUsed ?? false;
  if (options.toJokerLapUsed === true && !fromUsed) return true;
  if (options.toJokerLapUsed === false) return false;

  // Archives sans flag : comparer longueur main vs joker via effectiveMove.
  const budget = waypointBudget(circuit);
  const viaMain = buildTrackWaypointsToTarget(circuit, from, to, budget, fromUsed, {
    targetJokerLapUsed: false,
  });
  const viaJoker = buildTrackWaypointsToTarget(circuit, from, to, budget, fromUsed, {
    targetJokerLapUsed: true,
  });
  const mainOk = trackPositionsEqual(viaMain[viaMain.length - 1], to);
  const jokerOk = trackPositionsEqual(viaJoker[viaJoker.length - 1], to);
  const jokerVisits = viaJoker.some((wp) => wp.jokerBranchIndex != null);
  if (!jokerVisits || !jokerOk) return false;
  if (!mainOk) return true;

  const move = options.effectiveMove;
  if (typeof move === 'number' && move > 0) {
    const mainSteps = Math.max(0, viaMain.length - 1);
    const jokerSteps = Math.max(0, viaJoker.length - 1);
    if (Math.abs(jokerSteps - move) < Math.abs(mainSteps - move)) return true;
    if (move > mainSteps && move >= jokerSteps) return true;
  }

  // Dernier recours : joker nettement plus long (raccourci main vs branche).
  // Si les longueurs sont égales, on ne devine pas sans `toJokerLapUsed`.
  return viaJoker.length > viaMain.length + 1;
}

/** Waypoints densifiés from→to (joker / grille inclus), pour l’anim replay. */
export function archiveWaypointsBetween(
  circuit: Circuit,
  from: PlayerTrackPosition,
  to: PlayerTrackPosition,
  options: ArchiveWaypointOptions = {}
): PlayerTrackPosition[] {
  const fromUsed = options.fromJokerLapUsed ?? false;
  const targetJokerLapUsed = resolveArchiveTargetJokerLapUsed(circuit, from, to, options);
  const budget = waypointBudget(circuit);
  const bridge = buildTrackWaypointsToTarget(circuit, from, to, budget, fromUsed, {
    targetJokerLapUsed,
  });
  let result = densifyTrackWaypoints(circuit, bridge, targetJokerLapUsed);
  // Garde-fou : un wrap fantôme (~1 tour) si la cible n’est jamais atteinte.
  const maxSteps = budget;
  if (result.length - 1 > maxSteps) {
    result = densifyTrackWaypoints(circuit, [from, to], targetJokerLapUsed);
  }
  const move = options.effectiveMove;
  if (
    typeof move === 'number' &&
    move > 0 &&
    result.length - 1 > move + Math.max(4, Math.ceil(move * 0.5))
  ) {
    // Trop long vs le move réel → forcer le chemin joker/main cohérent une fois.
    const alt = buildTrackWaypointsToTarget(circuit, from, to, budget, fromUsed, {
      targetJokerLapUsed: !targetJokerLapUsed,
    });
    const altDense = densifyTrackWaypoints(circuit, alt, !targetJokerLapUsed);
    if (
      Math.abs(altDense.length - 1 - move) < Math.abs(result.length - 1 - move) &&
      trackPositionsEqual(altDense[altDense.length - 1], to)
    ) {
      result = altDense;
    }
  }
  return result;
}
